import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ZoomIn, ZoomOut, Maximize, Crown } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const NODE_W = 190, NODE_H = 62, H_GAP = 28, V_GAP = 78;
const ACCENT = { CORE_EXECUTIVE: "#2563EB", CONTROL: "#9333EA", CORE_LEAD: "#38BDF8", SPECIALIST: "#334155" };
const STATUS_DOT = { ACTIVE: "#10B981", STANDBY: "#64748B", WORKING: "#38BDF8" };

export default function OrgGraph({ agents }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [view, setView] = useState({ scale: 0.8, tx: 40, ty: 20 });
  const [vp, setVp] = useState({ w: 1000, h: 560 });
  const drag = useRef(null);
  const vpRef = useRef(null);

  useEffect(() => {
    const el = vpRef.current; if (!el) return;
    const upd = () => setVp({ w: el.clientWidth, h: el.clientHeight });
    upd();
    const ro = new ResizeObserver(upd); ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { nodes, lines, width, height } = useMemo(() => {
    const byCode = {}; agents.forEach((a) => (byCode[a.code] = a));
    const childrenMap = { FOUNDER: [] };
    agents.forEach((a) => { const k = a.manager_agent_code || "FOUNDER"; (childrenMap[k] = childrenMap[k] || []).push(a.code); });
    Object.values(childrenMap).forEach((arr) => arr.sort());
    const pos = {}; let cursor = 0; let maxDepth = 0;
    const place = (code, depth) => {
      maxDepth = Math.max(maxDepth, depth);
      const kids = childrenMap[code] || [];
      if (!kids.length) { const x = cursor * (NODE_W + H_GAP); cursor++; pos[code] = { x, depth }; return x; }
      const xs = kids.map((k) => place(k, depth + 1));
      const x = (xs[0] + xs[xs.length - 1]) / 2; pos[code] = { x, depth }; return x;
    };
    place("FOUNDER", 0);
    const nodes = Object.entries(pos).map(([code, p]) => ({ code, x: p.x, y: p.depth * (NODE_H + V_GAP), agent: code === "FOUNDER" ? null : byCode[code] }));
    const lines = [];
    Object.keys(childrenMap).forEach((parent) => {
      if (!pos[parent]) return;
      (childrenMap[parent] || []).forEach((c) => {
        if (!pos[c]) return;
        lines.push({ x1: pos[parent].x + NODE_W / 2, y1: pos[parent].depth * (NODE_H + V_GAP) + NODE_H, x2: pos[c].x + NODE_W / 2, y2: pos[c].depth * (NODE_H + V_GAP) });
      });
    });
    return { nodes, lines, width: cursor * (NODE_W + H_GAP) + 60, height: (maxDepth + 1) * (NODE_H + V_GAP) + 40 };
  }, [agents]);

  const onDown = (e) => { drag.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty }; };
  const onMove = (e) => { if (!drag.current) return; setView((v) => ({ ...v, tx: drag.current.tx + (e.clientX - drag.current.x), ty: drag.current.ty + (e.clientY - drag.current.y) })); };
  const onUp = () => { drag.current = null; };
  const zoom = (d) => setView((v) => ({ ...v, scale: Math.max(0.35, Math.min(1.6, +(v.scale + d).toFixed(2))) }));
  const reset = () => setView({ scale: 0.8, tx: 40, ty: 20 });
  const onWheel = (e) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); zoom(e.deltaY > 0 ? -0.1 : 0.1); } };

  // Minimap
  const MM_W = 190, MM_H = 120;
  const mmScale = Math.min(MM_W / width, MM_H / height);
  const mmW = width * mmScale, mmH = height * mmScale;
  const viewRect = { x: (-view.tx / view.scale) * mmScale, y: (-view.ty / view.scale) * mmScale, w: (vp.w / view.scale) * mmScale, h: (vp.h / view.scale) * mmScale };
  const mmClick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const gx = (e.clientX - r.left) / mmScale, gy = (e.clientY - r.top) / mmScale;
    setView((v) => ({ ...v, tx: -(gx * v.scale) + vp.w / 2, ty: -(gy * v.scale) + vp.h / 2 }));
  };

  return (
    <div className="m-card relative overflow-hidden" style={{ height: "72vh" }} data-testid="org-graph">
      <div className="absolute top-3 right-3 z-20 flex gap-1.5">
        <button onClick={() => zoom(0.1)} className="btn-ghost h-8 w-8 flex items-center justify-center" data-testid="org-zoom-in"><ZoomIn size={15} /></button>
        <button onClick={() => zoom(-0.1)} className="btn-ghost h-8 w-8 flex items-center justify-center" data-testid="org-zoom-out"><ZoomOut size={15} /></button>
        <button onClick={reset} className="btn-ghost h-8 w-8 flex items-center justify-center" data-testid="org-zoom-reset"><Maximize size={15} /></button>
      </div>
      <p className="absolute bottom-3 left-4 z-20 text-[10px] text-[#64748B] font-mono">{t("org.hint")}</p>

      <div ref={vpRef} className="w-full h-full cursor-grab active:cursor-grabbing" onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} onWheel={onWheel} style={{ touchAction: "none" }}>
        <div style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`, transformOrigin: "0 0", width, height, position: "relative" }}>
          <svg width={width} height={height} className="absolute inset-0 pointer-events-none">
            {lines.map((l, i) => (<path key={i} d={`M ${l.x1} ${l.y1} C ${l.x1} ${l.y1 + V_GAP / 2}, ${l.x2} ${l.y2 - V_GAP / 2}, ${l.x2} ${l.y2}`} stroke="#1E293B" strokeWidth="1.5" fill="none" />))}
          </svg>
          {nodes.map((n) => {
            if (!n.agent) return (
              <div key={n.code} style={{ position: "absolute", left: n.x, top: n.y, width: NODE_W, height: NODE_H }} className="rounded-xl flex items-center gap-3 px-3" data-testid="org-node-FOUNDER">
                <div className="absolute inset-0 rounded-xl" style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.25), rgba(37,99,235,0.05))", border: "1px solid rgba(37,99,235,0.5)" }} />
                <div className="relative h-9 w-9 rounded-lg bg-[#2563EB] flex items-center justify-center"><Crown size={17} className="text-white" /></div>
                <div className="relative"><p className="text-white text-sm font-bold">FOUNDER</p><p className="text-[10px] text-[#94A3B8]">{t("org.founderSub")}</p></div>
              </div>
            );
            const a = n.agent;
            return (
              <div key={n.code} onClick={() => navigate(`/workforce/${a.code}`)} style={{ position: "absolute", left: n.x, top: n.y, width: NODE_W, height: NODE_H, borderLeft: `3px solid ${ACCENT[a.employment_type] || "#334155"}` }} className="m-card m-card-hover flex items-center gap-2.5 px-3 cursor-pointer" data-testid={`org-node-${a.code}`}>
                <div className="h-8 w-8 rounded-lg bg-[#182238] border border-[#1E293B] flex items-center justify-center font-display font-bold text-white text-xs shrink-0">{a.name[0]}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5"><span className="text-white text-[13px] font-semibold truncate">{a.name}</span><span className="text-[9px] text-[#64748B] font-mono">{a.code}</span></div>
                  <p className="text-[10px] text-[#94A3B8] truncate leading-tight">{a.position}</p>
                </div>
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: STATUS_DOT[a.availability_status] || "#64748B" }} title={a.availability_status} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Minimap */}
      <div className="absolute bottom-3 right-3 z-20 rounded-lg overflow-hidden border border-[#1E293B] bg-[#0B0F19]/90 cursor-pointer" style={{ width: mmW, height: mmH }} onClick={mmClick} data-testid="org-minimap">
        <svg width={mmW} height={mmH}>
          {nodes.map((n) => (<rect key={n.code} x={n.x * mmScale} y={n.y * mmScale} width={NODE_W * mmScale} height={NODE_H * mmScale} rx="1.5" fill={n.agent ? (ACCENT[n.agent.employment_type] || "#334155") : "#2563EB"} opacity="0.85" />))}
          <rect x={viewRect.x} y={viewRect.y} width={viewRect.w} height={viewRect.h} fill="rgba(37,99,235,0.15)" stroke="#2563EB" strokeWidth="1.5" />
        </svg>
      </div>
    </div>
  );
}
